import { useEffect, useRef, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useI18N, useRenameList } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { useAtom } from "jotai";
import { savedPlaceIdToViewDataChangedAtom } from "@/state/atoms";
import Loader from "../Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { XIcon } from "lucide-react";
import WideButton from "../WideButton";

interface SavedPlace {
  cover_photo: string;
  created_at: string;
  id: number;
  name: string;
  saved_place_details: object;
  total_places: number;
  updated_at: string;
  user_id: number;
  // Add any other fields that are part of your saved place object
}

interface RenameListProps {
  onClose: () => void;
  list: SavedPlace;
}

const RenameList = ({ onClose, list }: RenameListProps) => {
  const isDark = useDarkMode();
  const i18n = useI18N();

  const showToast = useCustomToast();

  const [listName, setListName] = useState(list?.name);
  const { error, renameList, isLoading, result } = useRenameList();
  const [, setSavedPlaceIdToViewDataChanged] = useAtom(
    savedPlaceIdToViewDataChangedAtom
  );

  useEffect(() => {
    if (result) {
      AnalyticsService.trackGAEventHandler("renameListListRenamed");
      AnalyticsService.trackMx("renameListListRenamed", {
        SavedPlaceName: list?.name,
        SavedPlaceId: list?.id,
        SavedPlaceTotalPlaces: list?.total_places,
      });

      showToast(i18n.get("listRenamed"), "success");

      //close the rename list modal
      onClose();

      //main list detail page reload
      setSavedPlaceIdToViewDataChanged(true);
    }
  }, [
    result,
    i18n,
    onClose,
    isDark,
    listName,
    setSavedPlaceIdToViewDataChanged,
    list?.name,
    list?.id,
    list?.total_places,
  ]);

  useEffect(() => {
    if (error) {
      console.log("error", error);

      AnalyticsService.trackGAEventHandler(
        "renameListErrorSubmittingRenameList"
      );
      AnalyticsService.trackMx("renameListErrorSubmittingRenameList", {
        SavedPlaceName: list?.name,
        SavedPlaceId: list?.id,
        SavedPlaceTotalPlaces: list?.total_places,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark, list?.id, list?.name, list?.total_places]);

  const handleSave = () => {
    console.log("handleSave");

    if (!listName) {
      console.log("no listName", listName);
      return;
    }

    renameList({ name: listName, savedListId: list.id.toString() });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div>
      {isLoading && <Loader />}

      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(50vh-50px)] overflow-y-auto"
        role="dialog" aria-label={i18n.get("renameSavedList")} ref={firstFocusRef}  tabIndex={0}
      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            // tabIndex={0}
            // ref={firstFocusRef} // Attach the ref here
          >
            {i18n.get("renameSavedList")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="w-full md:max-w-[70%] mx-auto mt-5">
            <div className="flex flex-col gap-4 mb-2">
              <FloatingLabelInput
                label={i18n.get("yourListName")}
                required
                type="text"
                value={listName}
                placeholder={i18n.get("enterYourListName")}
                onChange={(event) => {
                  setListName(event.target.value);
                }}
                maxLength={50}
              />
            </div>

            <div className="mb-[10px] mt-[2px] flex-row">
              <div
                id="charCount"
                className="button-small-medium text-neutral-500 mt-3"
                tabIndex={0}
              >
                <span className={listName.length < 5 ? "text-red-600" : ""}>
                  {listName.length}
                </span>
                /50 Characters - at least 5 characters required
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex flex-col justify-center items-center w-full py-6 md:py-3  md:max-w-[60%]">
            <WideButton
              className="grow"
              disabled={listName.length < 3}
              onClick={() => {
                handleSave();
              }}
            >
              {i18n.get("rename")}
            </WideButton>
          </div>
        </footer>
        <button
          onClick={onClose}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </div>
  );
};

export default RenameList;
